import React, { useState } from "react";
import { FiLock, FiXCircle } from "react-icons/fi";
import toast from "react-hot-toast";
import { verify2FA } from "../api/auth-api"; // Uses the unified verify2FA function

/**
 * Component to handle the 6-digit code prompt for users who have already
 * set up 2FA and are logging in with their password + code.
 * It also handles the final verification step for the compulsory setup flow.
 * * @param {string} tempToken - The short-lived token received after password verification.
 * @param {function} onLoginSuccess - Callback to redirect to the dashboard upon successful verification.
 * @param {function} onLogout - Callback to clear state and return to the credentials login step.
 */
const TwoFactorPrompt = ({ tempToken, onLoginSuccess, onLogout }) => {
  const [tfaCode, setTfaCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleTfaSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!tempToken || tfaCode.length !== 6) {
        setError("Please enter a valid 6-digit code.");
        setLoading(false);
        return;
    }

    try {
        // Use the verify2FA function with the 6-digit code and the temporary token
        const res = await verify2FA(tfaCode, tempToken); // 🚀 API Call
        
        // This call should return the final token and user object on success
        localStorage.setItem("token", res.token);
        localStorage.setItem("user", JSON.stringify(res.user));

        toast.success("Verification successful!");
        onLoginSuccess(res.user.role); // Redirect to dashboard

    } catch (err) {
        console.error("2FA failed:", err);
        setError(err.message || "Invalid 2FA code. Please try again.");
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full">
      <div className="flex justify-end">
        <button 
            onClick={onLogout} 
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={loading}
        >
            <FiXCircle className="h-6 w-6" />
        </button>
      </div>
      
      <div className="text-center mb-6">
        <FiLock className="h-10 w-10 text-orange-500 mx-auto mb-2" />
        <h1 className="text-2xl font-semibold text-gray-800">Two-Factor Authentication</h1>
        <p className="text-sm text-gray-600 mt-2">
          Please enter the 6-digit code from your authenticator app.
        </p>
      </div>

      <form onSubmit={handleTfaSubmit} className="space-y-6">
        {error && (
            <div className="text-red-600 bg-red-100 border border-red-300 text-sm px-4 py-2 rounded-md text-center">
              {error}
            </div>
        )}

        <div>
          <label htmlFor="tfaCode" className="sr-only">
            Authentication Code
          </label>
          <input
            id="tfaCode"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={tfaCode}
            onChange={(e) => setTfaCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
            placeholder="123456"
            required
            autoFocus
            className="w-full px-4 py-3 text-center text-xl border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 tracking-widest font-mono"
            disabled={loading}
            maxLength={6}
          />
        </div>

        <button
          type="submit"
          disabled={loading || tfaCode.length !== 6}
          className="w-full bg-orange-500 text-white py-2 rounded-md hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {loading ? "Verifying..." : "Verify Code & Log In"}
        </button>
        
        <button
          type="button"
          onClick={onLogout}
          className="w-full text-xs text-gray-500 hover:text-gray-700 transition-colors"
          disabled={loading}
        >
          Cancel and Return to Password
        </button>
      </form>
    </div>
  );
};

export default TwoFactorPrompt;